import { useTranslation } from "@next-platform/client-common-core";
import useShouldBlurNsfwCover from "@next-platform/client-common-core/rulebook/cards/useShouldBlurNsfwCover";
import type { DiscoveredRulebook } from "@next-platform/client-common-core/rulebook/picker/rulebookDiscoverHit";
import { Button, ButtonSize, ButtonVariant, Icon, IconSize, IconVariants, Spinner, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import { type RulebookOwnerType, type RulebookSummary, RulebookVisibility } from "@next-platform/packages-ts-types";

import Trans from "@/components/Trans/Trans";
import { AvatarPreset } from "@/types";
import { getAvatarUrl } from "@/utils/getAvatarUrl";

import RulebookCard from "./RulebookCard";
import RulebookOwnerCopy from "./RulebookOwnerCopy";

type RulebookSectionProps = {
  /** What the Rulebook is attached to; picks the owner-specific description. */
  ownerType: RulebookOwnerType;
  attachedRulebook: RulebookSummary | null;
  /** A Rulebook is attached but isn't among the user's Rulebooks anymore (e.g. deleted). */
  isAttachedRulebookMissing?: boolean;
  isLoading?: boolean;
  /** The owner's current attachment couldn't be read; the actions are disabled until `onRetryLoad` succeeds. */
  hasLoadError?: boolean;
  onRetryLoad?: () => void;
  onAttach: () => void;
  onDetach: () => void;
  disabled?: boolean;
  dataTestId?: string;
};

type RulebookSectionActionsProps = {
  hasAttachedRulebook: boolean;
  isDisabled: boolean;
  onAttach: () => void;
  onDetach: () => void;
  dataTestId: string;
};

/** Attach (or Replace) plus Remove, as in the Lorebook section. */
const RulebookSectionActions = ({ hasAttachedRulebook, isDisabled, onAttach, onDetach, dataTestId }: RulebookSectionActionsProps) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-row gap-[7px]">
      <Button
        variant={ButtonVariant.OUTLINED}
        size={ButtonSize.MEDIUM}
        iconStart={hasAttachedRulebook ? IconVariants.REFRESH : IconVariants.PLUS}
        onClick={onAttach}
        disabled={isDisabled}
        type="button"
        className="rounded-[7px] border-gray-7"
        data-testid={`${dataTestId}-Attach`}
      >
        {hasAttachedRulebook ? (
          <Trans i18nKey="rulebookEditor:attach.button.replace" defaults="Replace Rulebook" />
        ) : (
          <Trans i18nKey="rulebookEditor:attach.button.attach" defaults="Attach Rulebook" />
        )}
      </Button>
      {hasAttachedRulebook ? (
        <Button
          variant={ButtonVariant.OUTLINED}
          size={ButtonSize.MEDIUM}
          iconStart={IconVariants.TRASH}
          onClick={onDetach}
          disabled={isDisabled}
          type="button"
          className="w-11 rounded-[7px] border-gray-7 px-0"
          aria-label={t("rulebookEditor:attach.button.remove", "Remove Rulebook")}
          data-testid={`${dataTestId}-Detach`}
        />
      ) : null}
    </div>
  );
};

type RulebookSectionBodyProps = Pick<RulebookSectionProps, "attachedRulebook" | "isAttachedRulebookMissing" | "hasLoadError" | "onRetryLoad"> & {
  dataTestId: string;
};

/** Whatever sits between the label and the buttons: the attached card, a "no longer available" note, or a load error with Retry. */
const RulebookSectionBody = ({ attachedRulebook, isAttachedRulebookMissing, hasLoadError, onRetryLoad, dataTestId }: RulebookSectionBodyProps) => {
  const shouldBlurNsfw = useShouldBlurNsfwCover();
  if (hasLoadError) {
    return (
      <div className="flex items-center gap-2" data-testid={`${dataTestId}-LoadError`}>
        <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-red-10">
          <Trans i18nKey="rulebookEditor:attach.loadError" defaults="Couldn't load the attached Rulebook." />
        </Text>
        <Button variant={ButtonVariant.TEXT} size={ButtonSize.SMALL} type="button" onClick={onRetryLoad} data-testid={`${dataTestId}-Retry`}>
          <Trans i18nKey="common:retry" defaults="Retry" />
        </Button>
      </div>
    );
  }
  if (isAttachedRulebookMissing) {
    return (
      <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-red-10" data-testid={`${dataTestId}-Missing`}>
        <Trans i18nKey="rulebookEditor:attach.missing" defaults="The attached Rulebook is no longer available. Replace or remove it." />
      </Text>
    );
  }
  if (!attachedRulebook) {
    return null;
  }
  // A Discover pick carries its cover's NSFW flag; keep it blurred here as it was in the picker.
  const isCoverBlurred = shouldBlurNsfw && Boolean((attachedRulebook as DiscoveredRulebook).isCoverNsfw);
  return (
    <RulebookCard
      variant="attached"
      dataTestId={`${dataTestId}-Card`}
      id={attachedRulebook.id}
      name={attachedRulebook.name}
      description={attachedRulebook.description}
      numMeters={attachedRulebook.num_meters}
      coverUrl={getAvatarUrl(attachedRulebook.cover_url ?? "", { preset: AvatarPreset.AVATAR_128X128 }) ?? undefined}
      isPrivate={attachedRulebook.visibility === RulebookVisibility.PRIVATE}
      isBlurred={isCoverBlurred}
    />
  );
};

/** Form section for the Rulebook attached to a character or persona — the Rulebook counterpart of `LorebookSection`. */
export default function RulebookSection({
  ownerType,
  attachedRulebook,
  isAttachedRulebookMissing = false,
  isLoading = false,
  hasLoadError = false,
  onRetryLoad,
  onAttach,
  onDetach,
  disabled = false,
  dataTestId = "RulebookSection",
}: RulebookSectionProps) {
  const hasAttachedRulebook = attachedRulebook !== null || isAttachedRulebookMissing;

  return (
    <div className="flex flex-col gap-2" data-testid={dataTestId}>
      <div className="flex items-center gap-1">
        <Text variant={TextVariants.LABEL_LARGE_MEDIUM} as="span" className="text-gray-12">
          <Trans i18nKey="rulebookEditor:attach.label" defaults="Rulebook" />
        </Text>
        <Icon icon={IconVariants.INFO} size={IconSize.SMALL} className="text-gray-11" />
      </div>

      {isLoading ? (
        <Spinner className="self-start" />
      ) : (
        <RulebookSectionBody
          attachedRulebook={attachedRulebook}
          isAttachedRulebookMissing={isAttachedRulebookMissing}
          hasLoadError={hasLoadError}
          onRetryLoad={onRetryLoad}
          dataTestId={dataTestId}
        />
      )}

      <RulebookSectionActions
        hasAttachedRulebook={hasAttachedRulebook}
        // Without the server attachment, Attach / Remove would act on a guess.
        isDisabled={disabled || isLoading || hasLoadError}
        onAttach={onAttach}
        onDetach={onDetach}
        dataTestId={dataTestId}
      />

      <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-gray-11">
        <RulebookOwnerCopy ownerType={ownerType} place="section" />
      </Text>
    </div>
  );
}
